import { Message } from "@/modules/chat/types";
import { initializeEcho, type EchoPresenceChannel } from "@/services/echo";

export interface PresenceParticipant {
  actor_type: "user" | "client";
  actor_id: number;
  display_name: string;
  role: "Manager" | "Client";
}

export type ConversationPresenceStatus = "online" | "offline" | "unknown";

export interface TypingParticipant extends PresenceParticipant {
  updated_at?: string;
}

type ChatRealtimeHandlers = {
  onMessage: (message: Message) => void;
  onTypingStarted: (participant: TypingParticipant) => void;
  onTypingStopped: (
    participant: Pick<TypingParticipant, "actor_type" | "actor_id">
  ) => void;
  onPresenceHere?: (participants: PresenceParticipant[]) => void;
  onPresenceJoining?: (participant: PresenceParticipant) => void;
  onPresenceLeaving?: (
    participant: Pick<PresenceParticipant, "actor_type" | "actor_id">
  ) => void;
  onError?: (error: unknown) => void;
};

export type ChatRealtimeSubscription = {
  leave: () => void;
};

type ChatRealtimeOptions = {
  includePresence?: boolean;
};

type EchoInstance = NonNullable<ReturnType<typeof initializeEcho>>;

let echoInstance: EchoInstance | null = null;

function realtimeEnabled(): boolean {
  return process.env.VUE_APP_CHAT_REALTIME !== "false";
}

async function getEcho(): Promise<EchoInstance | null> {
  if (!realtimeEnabled()) {
    return null;
  }

  if (echoInstance) {
    return echoInstance;
  }

  const created = initializeEcho();

  if (!created) {
    return null;
  }

  echoInstance = created;

  return echoInstance;
}

export async function subscribeToChatConversation(
  conversationId: number,
  handlers: ChatRealtimeHandlers,
  options: ChatRealtimeOptions = {}
): Promise<ChatRealtimeSubscription | null> {
  try {
    const echo = await getEcho();

    if (!echo) {
      return null;
    }

    const channelName = `chat.conversation.${conversationId}`;
    const privateChannel = echo.private(channelName);

    privateChannel
      .listen<{ message: Message }>(".chat.message.sent", (event) => {
        handlers.onMessage(event.message);
      })
      .listen(
        ".chat.typing.started",
        (event: { participant: TypingParticipant }) => {
          handlers.onTypingStarted(event.participant);
        }
      )
      .listen(
        ".chat.typing.stopped",
        (event: {
          participant: Pick<TypingParticipant, "actor_type" | "actor_id">;
        }) => {
          handlers.onTypingStopped(event.participant);
        }
      )
      .error((error: unknown) => {
        handlers.onError?.(error);
      });

    if (options.includePresence) {
      const presenceChannel = echo.join(channelName) as EchoPresenceChannel<
        PresenceParticipant[],
        PresenceParticipant,
        PresenceParticipant
      >;

      presenceChannel
        .here((participants: PresenceParticipant[]) => {
          handlers.onPresenceHere?.(participants);
        })
        .joining((participant: PresenceParticipant) => {
          handlers.onPresenceJoining?.(participant);
        })
        .leaving((participant: PresenceParticipant) => {
          handlers.onPresenceLeaving?.(participant);
        })
        .error((error: unknown) => {
          handlers.onError?.(error);
        });
    }

    return {
      leave: () => {
        echo.leave(channelName);
      },
    };
  } catch (error) {
    handlers.onError?.(error);
    return null;
  }
}
